import React, { useMemo, useState } from "react";
import { ModalV2, Text, Flex, Button } from "@jsq/ds/core";
import { ComplianceCaseTypeEnum as CaseTypeEnum } from "~/graphql/supergraphEnums";
import { useGetPicrPositionDetailsQuery } from "~/graphql/supergraphHooks";
import {
  type ComplianceCaseStatus,
  type ComplianceCaseTypeEnum,
  type GetComplianceCaseQuery,
} from "~/graphql/supergraphTypes";
import {
  EscalationModalInputHeader_Style,
  AdditionalTitle_Style,
} from "../CreateEscalationModal/styles_vanilla.css";
import ImpactedGPGrid from "../IssueGrid/ImpactedGPGrid";
import {
  ImpactedGPContext,
  useImpactedGPContext,
} from "../IssueGrid/ImpactedGPGrid/impactedGPcontext";
import * as rstyles from "./styles_vanilla.css";
import { getProfileNameForEscalation } from "./utils";

type EscalationType = NonNullable<
  NonNullable<GetComplianceCaseQuery["complianceCase"]>["escalations"]
>[0];

type Props = {
  show: boolean;
  onCloseModal: () => void;
  caseType: ComplianceCaseTypeEnum | undefined;
  caseStatus: ComplianceCaseStatus;
  caseId: number;
  escalation?: EscalationType | undefined;
  profilesForCase?: GetComplianceCaseQuery["complianceCase"] | null | undefined;
};

const ProposedEscalationsModal: React.FC<Props> = ({
  show,
  onCloseModal,
  caseType,
  caseStatus,
  caseId,
  escalation,
  profilesForCase,
}) => {
  const parentCtx = useImpactedGPContext();
  const [count, setCount] = useState<number>(0);
  const shouldUseInternalProvider = !parentCtx || parentCtx.setCount === undefined;
  const effectiveCount = shouldUseInternalProvider ? count : parentCtx.count;

  const isPicrCase = caseType === CaseTypeEnum.PaymentInstructionChangeReview;
  const { data: picrPositionData } = useGetPicrPositionDetailsQuery({
    variables: { caseId },
    skip: !isPicrCase,
  });

  const profileName = useMemo(
    () =>
      getProfileNameForEscalation(profilesForCase, escalation, isPicrCase, picrPositionData),
    [profilesForCase, escalation, isPicrCase, picrPositionData],
  );

  const modalBody = (
    <ModalV2 show={show} isCentered={false} onHide={onCloseModal} title="Proposed escalations">
      <ModalV2.Body>
        <Flex flexDirection="column" gap="16px">
          <div className={rstyles.row}>
            <Text
              className={`${EscalationModalInputHeader_Style} ${rstyles.label}`}
              variant="strong-md"
            >
              Profile
            </Text>
            <Text className={rstyles.value}>{profileName}</Text>
          </div>
          <div className={rstyles.row}>
            <Text
              className={`${EscalationModalInputHeader_Style} ${rstyles.label}`}
              variant="strong-md"
            >
              Escalation issue
            </Text>
            <Text className={rstyles.value}>{escalation?.description || ""}</Text>
          </div>

          {caseType && (
            <Text variant="strong-md" className={AdditionalTitle_Style}>
              {effectiveCount
                ? `Additional escalation cases will be created for ${effectiveCount} ${effectiveCount > 1 ? "GPs" : "GP"}`
                : ""}
            </Text>
          )}

          {caseType && (
            <ImpactedGPGrid
              caseId={caseId}
              caseType={caseType}
              profileId={escalation?.profileId ?? profilesForCase?.investingProfile?.id ?? 0}
              caseStatus={caseStatus}
            />
          )}
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex justifyContent="flex-end">
          <Button variant="neutral" onClick={onCloseModal}>
            Close
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );

  if (shouldUseInternalProvider) {
    return (
      <ImpactedGPContext.Provider value={{ count, setCount }}>
        {modalBody}
      </ImpactedGPContext.Provider>
    );
  }
  return modalBody;
};

export default ProposedEscalationsModal;
